import { ChevronDown, ChevronUp } from "lucide-react";
import type { CSSProperties } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "~/lib/utils";

interface JsonTableProps {
  /** Value to render. When `source` is also given, this is the "after" side of a diff. */
  target: unknown;
  /** Optional "before" value — enables diff mode (added/removed/modified highlighting). */
  source?: unknown;
  /** `"column"` (default) lays keys down the left; `"row"` lays keys across a header row. */
  direction?: "column" | "row";
  width?: CSSProperties["width"];
  headWidth?: CSSProperties["width"];
  /** Rows shown before the expand/collapse toggle appears. */
  maxRows?: number;
  className?: string;
}

type DiffType = "added" | "removed";

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Renders an arbitrary JSON value as a recursive, nested key-value table.
 * Tailwind/shadcn port of V1's `JsonTable` — same behaviour (diff mode via `source`,
 * `direction` row/column, `width`/`headWidth`, expand/collapse), restyled.
 */
export function JsonTable({
  target,
  source,
  direction = "column",
  width,
  headWidth,
  maxRows = 3,
  className,
}: JsonTableProps) {
  let body: React.ReactNode;
  if (Array.isArray(source) || Array.isArray(target)) {
    body = (
      <ArrayList
        value={Array.isArray(source) ? source : undefined}
        targetValue={Array.isArray(target) ? target : undefined}
        maxRows={maxRows}
      />
    );
  } else if (isObject(target) || isObject(source)) {
    body =
      direction === "row" ? (
        <JsonRowTable source={source} target={target} width={width} headWidth={headWidth} maxRows={maxRows} />
      ) : (
        <JsonColumnTable source={source} target={target} width={width} headWidth={headWidth} maxRows={maxRows} />
      );
  } else {
    // Top-level primitive / null — render as a bare leaf value.
    body = <JsonLeaf value={target ?? source} maxRows={maxRows} />;
  }

  return <div className={cn("overflow-x-auto", className)}>{body}</div>;
}

/** Renders a single JSON leaf value; recurses into a nested table for objects/arrays. */
function JsonLeaf({ value, maxRows }: { value: unknown; maxRows: number }) {
  if (value === null || value === undefined) return <span className="text-fg-3">—</span>;
  if (Array.isArray(value) || isObject(value)) return <JsonTable target={value} maxRows={maxRows} />;
  if (typeof value === "string") {
    return <span className="whitespace-pre-wrap break-words [&_a]:text-fg-link [&_a]:underline">{linkify(value)}</span>;
  }
  return <span className="whitespace-nowrap tabular-nums">{String(value)}</span>;
}

const URL_RE = /(https?:\/\/[^\s]+)/g;
const URL_MAX_LEN = 50;

/**
 * Linkifies bare URLs in a string (V1 `JsonValue` parity) while rendering every
 * segment as a React node so non-URL text is auto-escaped — no `dangerouslySetInnerHTML`,
 * so raw `<script>`/`<img onerror>` in a value can never execute.
 */
function linkify(text: string): React.ReactNode {
  // split() with a capturing group puts URLs at the odd indices, plain text at the even ones.
  return text.split(URL_RE).map((seg, i) =>
    i % 2 === 1 ? (
      <a key={i} href={seg} target="_blank" rel="noreferrer">
        {seg.length > URL_MAX_LEN ? `${seg.slice(0, URL_MAX_LEN)}...` : seg}
      </a>
    ) : (
      seg
    ),
  );
}

function compareValues(source: unknown, target: unknown) {
  const isDifferent = source !== undefined && target !== undefined && JSON.stringify(source) !== JSON.stringify(target);
  const onlyInSource = source !== undefined && target === undefined;
  const onlyInTarget = source === undefined && target !== undefined;
  return { isDifferent, onlyInSource, onlyInTarget };
}

function DiffBox({ type, children, className }: { type: DiffType; children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-[var(--radius-xs)] px-1.5 py-0.5 text-[13px]",
        type === "added" ? "bg-success/10" : "bg-danger/10",
        className,
      )}
    >
      <span className={cn("font-medium", type === "added" ? "text-success" : "text-danger")}>
        {type === "added" ? "+" : "-"}
      </span>
      <span>{children}</span>
    </span>
  );
}

function ArrayList({ value, targetValue, maxRows }: { value?: unknown[]; targetValue?: unknown[]; maxRows: number }) {
  const maxLength = Math.max(value?.length ?? 0, targetValue?.length ?? 0);
  if (maxLength === 0) return <span className="text-fg-3">—</span>;
  const isDiff = value !== undefined && targetValue !== undefined;

  return (
    <ul className="inline-block space-y-1 align-top">
      {Array.from({ length: maxLength }).map((_, i) => {
        const valueItem = value?.[i];
        const targetItem = targetValue?.[i];
        const { isDifferent, onlyInSource, onlyInTarget } = compareValues(valueItem, targetItem);
        return (
          <li key={i} className="border-b border-border-default pb-1 last:border-b-0 last:pb-0">
            {isDiff ? (
              <>
                {(isDifferent || onlyInSource) && valueItem !== undefined && (
                  <DiffBox type="removed">
                    <JsonLeaf value={valueItem} maxRows={maxRows} />
                  </DiffBox>
                )}
                {!isDifferent && !onlyInSource && !onlyInTarget && targetItem !== undefined && (
                  <JsonLeaf value={targetItem} maxRows={maxRows} />
                )}
                {onlyInTarget && targetItem !== undefined && (
                  <DiffBox type="added">
                    <JsonLeaf value={targetItem} maxRows={maxRows} />
                  </DiffBox>
                )}
                {isDifferent && targetItem !== undefined && (
                  <DiffBox type="added" className="ml-1">
                    <JsonLeaf value={targetItem} maxRows={maxRows} />
                  </DiffBox>
                )}
              </>
            ) : (
              <JsonLeaf value={(valueItem ?? targetItem) as unknown} maxRows={maxRows} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function CellContent({
  source,
  target,
  isDiff,
  maxRows,
}: {
  source: unknown;
  target: unknown;
  isDiff: boolean;
  maxRows: number;
}) {
  if (Array.isArray(source) || Array.isArray(target)) {
    return (
      <ArrayList
        value={Array.isArray(source) ? source : undefined}
        targetValue={Array.isArray(target) ? target : undefined}
        maxRows={maxRows}
      />
    );
  }

  if (!isDiff) {
    return target !== undefined ? <JsonLeaf value={target} maxRows={maxRows} /> : null;
  }

  const { isDifferent, onlyInSource, onlyInTarget } = compareValues(source, target);
  return (
    <>
      {(isDifferent || onlyInSource) && source !== undefined && (
        <DiffBox type="removed">
          <JsonLeaf value={source} maxRows={maxRows} />
        </DiffBox>
      )}
      {!isDifferent && !onlyInSource && !onlyInTarget && target !== undefined && (
        <JsonLeaf value={target} maxRows={maxRows} />
      )}
      {!isDifferent && !onlyInSource && !onlyInTarget && target === undefined && source !== undefined && (
        <JsonLeaf value={source} maxRows={maxRows} />
      )}
      {(isDifferent || onlyInTarget) && target !== undefined && source !== undefined && (
        <DiffBox type="added" className={isDifferent ? "ml-1" : undefined}>
          <JsonLeaf value={target} maxRows={maxRows} />
        </DiffBox>
      )}
    </>
  );
}

function useJsonTable(source: unknown, target: unknown, maxRows: number) {
  const [expanded, setExpanded] = useState(false);
  const allKeys = new Set([
    ...(isObject(source) ? Object.keys(source) : []),
    ...(isObject(target) ? Object.keys(target) : []),
  ]);
  const keysArray = Array.from(allKeys);
  const displayKeys = expanded ? keysArray : keysArray.slice(0, maxRows);
  const hasMore = keysArray.length > maxRows;
  return { expanded, setExpanded, displayKeys, hasMore };
}

function ExpandToggle({
  expanded,
  onToggle,
  className,
}: {
  expanded: boolean;
  onToggle: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={expanded ? t("ui.collapse") : t("ui.expand")}
      className={cn(
        "flex w-full items-center justify-center border-t border-border-default py-1 text-fg-3 hover:bg-bg-subtle",
        className,
      )}
    >
      {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </button>
  );
}

function JsonColumnTable({
  source,
  target,
  width,
  headWidth,
  maxRows,
}: Pick<JsonTableProps, "source" | "target" | "width" | "headWidth" | "maxRows"> & { maxRows: number }) {
  const { expanded, setExpanded, displayKeys, hasMore } = useJsonTable(source, target, maxRows);
  const isDiff = source !== undefined;
  const sourceObj = isObject(source) ? source : undefined;
  const targetObj = isObject(target) ? target : undefined;

  if (displayKeys.length === 0) return <span className="text-fg-3">—</span>;

  return (
    <div className="inline-block overflow-hidden rounded-[var(--radius-xs)] border border-border-default bg-white align-top">
      <table className="border-collapse text-[13px]" style={{ width: width ?? "100%" }}>
        <tbody>
          {displayKeys.map((key) => {
            const sourceValue = sourceObj?.[key];
            const targetValue = targetObj?.[key];
            const { onlyInSource, onlyInTarget } = compareValues(sourceValue, targetValue);

            if (isDiff && (onlyInSource || onlyInTarget)) {
              const value = onlyInSource ? sourceValue : targetValue;
              return (
                <tr key={key} className="border-b border-border-default align-top last:border-b-0">
                  <td colSpan={2} className="px-3 py-2">
                    <DiffBox type={onlyInSource ? "removed" : "added"} className="items-center">
                      <span className="mr-0.5">{key}:</span>
                      {value !== undefined ? <JsonLeaf value={value} maxRows={maxRows} /> : "undefined"}
                    </DiffBox>
                  </td>
                </tr>
              );
            }

            return (
              <tr key={key} className="border-b border-border-default align-top last:border-b-0">
                <th
                  className="whitespace-nowrap border-r border-border-default bg-bg-subtle px-3 py-2 text-left align-top font-bold text-fg-1"
                  style={{ width: headWidth ?? "auto" }}
                >
                  {key}
                </th>
                <td className="px-3 py-2 text-fg-2">
                  <CellContent source={sourceValue} target={targetValue} isDiff={isDiff} maxRows={maxRows} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {hasMore && <ExpandToggle expanded={expanded} onToggle={() => setExpanded((v) => !v)} />}
    </div>
  );
}

function JsonRowTable({
  source,
  target,
  width,
  headWidth,
  maxRows,
}: Pick<JsonTableProps, "source" | "target" | "width" | "headWidth" | "maxRows"> & { maxRows: number }) {
  const { expanded, setExpanded, displayKeys, hasMore } = useJsonTable(source, target, maxRows);
  const isDiff = source !== undefined;
  const sourceObj = isObject(source) ? source : undefined;
  const targetObj = isObject(target) ? target : undefined;

  if (displayKeys.length === 0) return <span className="text-fg-3">—</span>;

  return (
    <div className="inline-block overflow-hidden rounded-[var(--radius-xs)] border border-border-default bg-white align-top">
      <table className="border-collapse text-[13px]" style={{ width: width ?? "100%" }}>
        <thead>
          <tr className="border-b border-border-default">
            {displayKeys.map((key) => {
              const { onlyInSource, onlyInTarget } = compareValues(sourceObj?.[key], targetObj?.[key]);
              return (
                <th
                  key={key}
                  className="border-r border-border-default bg-bg-subtle px-3 py-2 text-left align-top font-bold text-fg-1 last:border-r-0"
                  style={{ width: headWidth ?? "auto" }}
                >
                  {isDiff && (onlyInSource || onlyInTarget) ? (
                    <DiffBox type={onlyInSource ? "removed" : "added"}>{key}</DiffBox>
                  ) : (
                    key
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr className="align-top">
            {displayKeys.map((key) => {
              const sourceValue = sourceObj?.[key];
              const targetValue = targetObj?.[key];
              const { onlyInSource, onlyInTarget } = compareValues(sourceValue, targetValue);

              if (isDiff && (onlyInSource || onlyInTarget)) {
                const value = onlyInSource ? sourceValue : targetValue;
                return (
                  <td key={key} className="border-r border-border-default px-3 py-2 last:border-r-0">
                    <DiffBox type={onlyInSource ? "removed" : "added"}>
                      {value !== undefined ? <JsonLeaf value={value} maxRows={maxRows} /> : "undefined"}
                    </DiffBox>
                  </td>
                );
              }

              return (
                <td key={key} className="border-r border-border-default px-3 py-2 text-fg-2 last:border-r-0">
                  <CellContent source={sourceValue} target={targetValue} isDiff={isDiff} maxRows={maxRows} />
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
      {hasMore && <ExpandToggle expanded={expanded} onToggle={() => setExpanded((v) => !v)} />}
    </div>
  );
}
